// Web App Test Status
// --------------------
// Mirrors the back end or graph call
// Supposed to be a simple storage container
//
// Note we replace underscores if they occur
//
import { convertFromGraphAPItoUI } from "@/utils/transform";
import {
  ALL_FIXED_PHASE_VALUE,
  EMPTY_STRING,
  EPOCH_TO_JS_DATE_CONVERSION,
  REMEDIATION_IN_PROGRESS_PHASE_VALUE,
  RISK_RATING_CRITICAL,
  RISK_RATING_HIGH,
  RISK_RATING_LOW,
  RISK_RATING_MED,
  RESULTS_READY_PHASE_VALUE,
  VULN_STATUS_OPEN,
} from "../constants/definitions";

// Graph Imports
import { getWebAppTestStatusFromGraph } from "../services/dataListOperations.service";

// Risk Score Multipliers
const SCORE_MULTIPLIER_CRITICAL = 10;
const SCORE_MULTIPLIER_HIGH = 5;
const SCORE_MULTIPLIER_MED = 3;
const SCORE_MULTIPLIER_LOW = 1;

interface WebAppTestStatusData {
  lastTestDate: number;
  currentPhase: string;
  allcrits: number;
  allhighs: number;
  allmeds: number;
  alllows: number;
  opencrits: number;
  openhighs: number;
  openmeds: number;
  openlows: number;
  score: number;
}

class WebAppTestStatus {
  lastTestDate: Date;
  currentPhase: string;
  percentComplete: number;
  allcrits: number;
  allhighs: number;
  allmeds: number;
  alllows: number;
  opencrits: number;
  openhighs: number;
  openmeds: number;
  openlows: number;
  riskscore: number;

  constructor(webappid?: string) {
    this.lastTestDate = new Date();
    this.currentPhase = EMPTY_STRING;
    this.percentComplete = 0;
    this.allcrits = 0;
    this.allhighs = 0;
    this.allmeds = 0;
    this.alllows = 0;
    this.opencrits = 0;
    this.openhighs = 0;
    this.openmeds = 0;
    this.openlows = 0;
    this.riskscore = 0;

    if (webappid) {
      this.getWebAppTestStatus(webappid);
    }
  }

  // Populate from pre-fetched data (e.g. from listWebAppTestStatuses batch call).
  // Applies the same field mapping and derived calculations as getWebAppTestStatus
  // but without making an API call.
  populateFromData(data: WebAppTestStatusData): void {
    if (!data) return;
    this.lastTestDate = new Date(EPOCH_TO_JS_DATE_CONVERSION * data.lastTestDate);
    this.currentPhase = convertFromGraphAPItoUI(data.currentPhase);
    this.allcrits = data.allcrits ?? 0;
    this.allhighs = data.allhighs ?? 0;
    this.allmeds = data.allmeds ?? 0;
    this.alllows = data.alllows ?? 0;
    this.opencrits = data.opencrits ?? 0;
    this.openhighs = data.openhighs ?? 0;
    this.openmeds = data.openmeds ?? 0;
    this.openlows = data.openlows ?? 0;
    this.calcPercentComplete();
    this.calcRiskScore();
  }

  // Get Web Application Test Status
  // --------------------------------
  //
  getWebAppTestStatus(webappid: string): void {
    getWebAppTestStatusFromGraph(webappid)
      .then((data: WebAppTestStatusData) => {
        if (data) {
          this.lastTestDate = new Date(
            EPOCH_TO_JS_DATE_CONVERSION * data.lastTestDate
          );
          this.currentPhase = convertFromGraphAPItoUI(data.currentPhase);
          this.allcrits = data.allcrits ?? 0;
          this.allhighs = data.allhighs;
          this.allmeds = data.allmeds;
          this.alllows = data.alllows;
          this.opencrits = data.opencrits ?? 0;
          this.openhighs = data.openhighs;
          this.openmeds = data.openmeds;
          this.openlows = data.openlows;
          this.riskscore = data.score;

          // Calculate the percentage complete - safety check on the exceeding 100 mark
          this.calcPercentComplete();

          // Calculate the risk score
          this.calcRiskScore();
        }
      })
      .catch(() => {
        // Swallow errors — most commonly "No current user" when the Cognito
        // session is cleared during signout while this background fetch is in flight.
      });
  }

  // Update Web App Test Status
  // ----------------------------
  // Called to update the web app test status on the front end when a vuln is changed
  //
  // Note that the risk and status never change at the same time in the current architecture
  // However the algorithm here is to assume they do
  // So update the risk keeping the status the same, then move the status
  //
  updateWebAppTestStatus(
    oldRisk: string,
    newRisk: string,
    oldStatus: string,
    newStatus: string
  ): void {
    // If the risk level has changed then we need to change the count
    if (newRisk !== oldRisk) {
      if (VULN_STATUS_OPEN === oldStatus) {
        // If the old status was open then we are only changing the risk on an open high/med/low

        // Decrement the previous risk
        if (RISK_RATING_CRITICAL === oldRisk) {
          this.opencrits -= 1;
        } else if (RISK_RATING_HIGH === oldRisk) {
          this.openhighs -= 1;
        } else if (RISK_RATING_MED === oldRisk) {
          this.openmeds -= 1;
        } else if (RISK_RATING_LOW === oldRisk) {
          this.openlows -= 1;
        }

        // Increment the new risk
        if (RISK_RATING_CRITICAL === newRisk) {
          this.opencrits += 1;
        } else if (RISK_RATING_HIGH === newRisk) {
          this.openhighs += 1;
        } else if (RISK_RATING_MED === newRisk) {
          this.openmeds += 1;
        } else if (RISK_RATING_LOW === newRisk) {
          this.openlows += 1;
        }
      }

      // For all risks, regardless of status, update the all count
      if (RISK_RATING_CRITICAL === oldRisk) {
        this.allcrits -= 1;
      } else if (RISK_RATING_HIGH === oldRisk) {
        this.allhighs -= 1;
      } else if (RISK_RATING_MED === oldRisk) {
        this.allmeds -= 1;
      } else if (RISK_RATING_LOW === oldRisk) {
        this.alllows -= 1;
      }
      if (RISK_RATING_CRITICAL === newRisk) {
        this.allcrits += 1;
      } else if (RISK_RATING_HIGH === newRisk) {
        this.allhighs += 1;
      } else if (RISK_RATING_MED === newRisk) {
        this.allmeds += 1;
      } else if (RISK_RATING_LOW === newRisk) {
        this.alllows += 1;
      }
    }

    // If the vuln status has changed then we need to change the count
    if (newStatus !== oldStatus) {
      if (VULN_STATUS_OPEN === oldStatus) {
        if (RISK_RATING_CRITICAL === newRisk) {
          this.opencrits -= 1;
        } else if (RISK_RATING_HIGH === newRisk) {
          this.openhighs -= 1;
        } else if (RISK_RATING_MED === newRisk) {
          this.openmeds -= 1;
        } else if (RISK_RATING_LOW === newRisk) {
          this.openlows -= 1;
        }
      }
      if (VULN_STATUS_OPEN === newStatus) {
        if (RISK_RATING_CRITICAL === newRisk) {
          this.opencrits += 1;
        } else if (RISK_RATING_HIGH === newRisk) {
          this.openhighs += 1;
        } else if (RISK_RATING_MED === newRisk) {
          this.openmeds += 1;
        } else if (RISK_RATING_LOW === newRisk) {
          this.openlows += 1;
        }
      }
    }

    // Sanity check — no count below 0
    this.opencrits = Math.max(0, this.opencrits);
    this.openhighs = Math.max(0, this.openhighs);
    this.openmeds = Math.max(0, this.openmeds);
    this.openlows = Math.max(0, this.openlows);
    this.allcrits = Math.max(0, this.allcrits);
    this.allhighs = Math.max(0, this.allhighs);
    this.allmeds = Math.max(0, this.allmeds);
    this.alllows = Math.max(0, this.alllows);

    var openCount = this.opencrits + this.openhighs + this.openmeds + this.openlows;
    var allCount = this.allcrits + this.allhighs + this.allmeds + this.alllows;

    // Update the status
    if (openCount === allCount && allCount > 0) {
      // No vulns have been fixed, so we are still in the view phase
      // Note: In the event that there are no vulns we can go straight to complete phase
      this.currentPhase = RESULTS_READY_PHASE_VALUE;
    } else if (0 === openCount) {
      // All vulns have been remediated
      this.currentPhase = ALL_FIXED_PHASE_VALUE;
    } else {
      // Some vulns are fixed, work still in progress
      this.currentPhase = REMEDIATION_IN_PROGRESS_PHASE_VALUE;
    }

    this.calcPercentComplete();
    this.calcRiskScore();
  }

  calcPercentComplete(): void {
    var openCount = this.opencrits + this.openhighs + this.openmeds + this.openlows;
    var allCount = this.allcrits + this.allhighs + this.allmeds + this.alllows;

    if (allCount > 0) {
      var percentComplete = Math.floor(
        100 * ((allCount - openCount) / allCount)
      );
      this.percentComplete = percentComplete > 100 ? 100 : percentComplete;
    } else {
      this.percentComplete = 0;
    }
  }

  calcRiskScore(): void {
    this.riskscore =
      this.opencrits * SCORE_MULTIPLIER_CRITICAL +
      this.openhighs * SCORE_MULTIPLIER_HIGH +
      this.openmeds * SCORE_MULTIPLIER_MED +
      this.openlows * SCORE_MULTIPLIER_LOW;
  }
}

export default WebAppTestStatus;
